import { useCallback, useEffect, useState } from "react";
import { Link } from "react-router-dom";
import {
  Card,
  CardContent,
  CardHeader,
  CardTitle,
  CardDescription,
} from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { api } from "@/lib/api";
import { onDashboardChanged } from "@/lib/dashboardWs";
import { parseCompileJobs, sequenceText } from "@/lib/buildLabels";
import {
  attemptModeLabel,
  parseTimestamp,
  TERMINAL_STATUSES,
} from "@/lib/versionState";

/**
 * Publish-pipeline attempts that are queued or running, for the Logs page
 * (2026-10-12 operator report: a queued republish/rebuild showed up
 * nowhere — the compile jobs below only exist once a worker picks the
 * request up and submits step 1, which can be hours behind other runs).
 * Reads the same GET /v1/publish/packages the Registry page does; a queued
 * request is the `pending` row internal/publish records the moment it is
 * enqueued, carrying what was queued (run_mode) and who asked
 * (requested_by).
 */
interface ActiveRow {
  package_name: string;
  version: string;
  compiler_version: string | null;
  status: string;
  job_id_1: string | null;
  job_id_2: string | null;
  job_id_3: string | null;
  run_mode?: string | null;
  requested_by?: string | null;
  /** JSON CompileJobRecord[] (private/auth migration 0081): every build of
   *  the attempt so far, in order — see lib/buildLabels.ts. */
  compile_jobs?: string | null;
  updated_at: string;
}

/** The build the attempt is working on right now ("3/5 LLVM instrumented"),
 *  and its compile job id when it is a compile (a training run has none). */
function currentBuild(
  v: ActiveRow,
): { text: string; jobId: string | null } | null {
  const records = parseCompileJobs(v.compile_jobs);
  const running = records.filter((r) => r.status === "running").at(-1);
  const r = running ?? records.at(-1);
  if (!r) return null;
  const seq = sequenceText(r.seq, records.length);
  return {
    text: `${seq ? `${seq} ` : ""}${r.label}`,
    jobId: r.jobId ?? null,
  };
}

const STEP_LABEL: Record<string, string> = {
  pending: "queued",
  compiling_package_test: "compiling (package test)",
  testing_package_test: "testing (package test)",
  compiling_full_project: "compiling (full project)",
  testing_full_project: "testing (full project)",
  compiling_addon: "compiling (addon)",
  verifying_addon: "verifying (addon)",
  publishing: "publishing",
  rebenching_macos: "re-running macOS benchmark",
};

export function PublishActivityCard({ token }: { token: string }) {
  const [rows, setRows] = useState<ActiveRow[] | null>(null);
  const [failed, setFailed] = useState(false);

  const load = useCallback(() => {
    api<{ ok: true; packages: Array<{ versions: ActiveRow[] }> }>(
      token,
      "/publish/packages",
    )
      .then((r) => {
        setRows(
          r.packages
            .flatMap((p) => p.versions)
            .filter((v) => !TERMINAL_STATUSES.has(v.status))
            .sort(
              (a, b) =>
                parseTimestamp(b.updated_at) - parseTimestamp(a.updated_at),
            ),
        );
        setFailed(false);
      })
      .catch(() => setFailed(true));
  }, [token]);

  useEffect(load, [load]);
  useEffect(() => onDashboardChanged(token, load), [token, load]);
  useEffect(() => {
    const t = setInterval(load, 10_000);
    return () => clearInterval(t);
  }, [load]);

  return (
    <Card>
      <CardHeader>
        <CardTitle>Publish pipeline</CardTitle>
        <CardDescription>
          Registry publish attempts that are queued or running. A queued attempt
          has no compile job yet; its jobs appear in “Recent jobs” once a
          publish worker starts it.{" "}
          <Link to="/registry" className="underline">
            Open Registry
          </Link>
        </CardDescription>
      </CardHeader>
      <CardContent>
        {failed && (
          <p className="text-sm text-muted-foreground">
            Couldn’t load publish activity.
          </p>
        )}
        {rows?.length === 0 && (
          <p className="text-sm text-muted-foreground">
            Nothing queued or running.
          </p>
        )}
        <ul className="space-y-1.5">
          {rows?.map((v) => {
            const cur = currentBuild(v);
            const job = cur?.jobId ?? v.job_id_3 ?? v.job_id_2 ?? v.job_id_1;
            const mode = attemptModeLabel(v.run_mode);
            return (
              <li
                key={`${v.package_name}:${v.version}:${v.compiler_version ?? ""}`}
                className="flex flex-wrap items-center gap-x-3 gap-y-1 text-sm"
              >
                <span className="font-mono">
                  {v.package_name}@{v.version}
                </span>
                <Badge
                  variant={v.status === "pending" ? "secondary" : "default"}
                  className={
                    v.status === "pending" ? undefined : "animate-pulse"
                  }
                >
                  {STEP_LABEL[v.status] ?? v.status}
                </Badge>
                {mode && (
                  <span className="text-xs text-muted-foreground">
                    {mode}
                    {v.requested_by ? ` · requested by ${v.requested_by}` : ""}
                  </span>
                )}
                <span className="font-mono text-xs text-muted-foreground">
                  compiler {v.compiler_version ?? "—"}
                </span>
                {cur && (
                  <Badge variant="info" title="The build this attempt is on">
                    {cur.text}
                  </Badge>
                )}
                {job && (
                  <Link
                    to={`/logs/${encodeURIComponent(job)}`}
                    className="font-mono text-xs underline decoration-dotted"
                  >
                    job {job.slice(0, 8)}
                  </Link>
                )}
              </li>
            );
          })}
        </ul>
      </CardContent>
    </Card>
  );
}
